import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import AlignBottomIcon from '@duyank/icons/regular/AlignBottom';
import AlignCenterHorizontalIcon from '@duyank/icons/regular/AlignCenterHorizontal';
import AlignCenterVerticalIcon from '@duyank/icons/regular/AlignCenterVertical';
import AlignLeftIcon from '@duyank/icons/regular/AlignLeft';
import AlignRightIcon from '@duyank/icons/regular/AlignRight';
import AlignTopIcon from '@duyank/icons/regular/AlignTop';
import CaretCircleDoubleDownIcon from '@duyank/icons/regular/CaretCircleDoubleDown';
import CaretCircleDoubleUpIcon from '@duyank/icons/regular/CaretCircleDoubleUp';
import CaretCircleDownIcon from '@duyank/icons/regular/CaretCircleDown';
import CaretCircleUpIcon from '@duyank/icons/regular/CaretCircleUp';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import SelectionBackgroundIcon from '@duyank/icons/regular/SelectionBackground';
import XIcon from '@duyank/icons/regular/X';
import { LayerId } from '@lidojs/design-core';
import { getPosition } from '@lidojs/design-utils';
import reverse from 'lodash/reverse';
import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useEditor, useSelectedLayers } from '../../hooks';
import { PageContext } from '../../layers/core/PageContext';
import ReverseTransformLayer from './layer/ReverseTransformLayer';
import LayerContent from './LayerContent';
import Sidebar, { SidebarProps } from './Sidebar';
import SortableLayer from './SortableLayer';

type LayerSidebarProps = SidebarProps;
const LayerSidebar: ForwardRefRenderFunction<
  HTMLDivElement,
  LayerSidebarProps
> = ({ ...props }, ref) => {
  const dataRef = useRef({ isMultipleSelect: false });
  const { selectedLayerIds } = useSelectedLayers();
  const [activeId, setActiveId] = useState<LayerId | null>(null);
  const [tab, setTab] = useState('arrange');
  const { layers, actions, activePage, pageIndex } = useEditor((state) => ({
    layers:
      state.pages[state.activePage] && state.pages[state.activePage].layers,
      pageIndex: state.activePage,
    activePage: state.activePage,
  }));
  const layerList = useMemo(() => {
    if (!layers) {
      return;
    }
    return reverse(layers['ROOT'].data.child.map((layerId) => layers[layerId]));
  }, [layers]);
  const rootLayer = useMemo(() => {
    if (!layers) {
      return;
    }
    return layers.ROOT;
  }, [layers]);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over) {
      return;
    }
    if (active.id !== over.id) {
      const newIndex =
        layerList.length - layerList.findIndex((f) => f.id === over.id) - 1;
      actions.moveLayerPosition(activePage, active.id, newIndex);
    }
  };

  const handleClickOption = (e: React.MouseEvent) => {
    actions.showContextMenu(getPosition(e.nativeEvent));
  };
  useEffect(() => {
    const enableMultipleSelect = (e: KeyboardEvent) => {
      dataRef.current.isMultipleSelect = e.shiftKey;
    };
    window.addEventListener('keydown', enableMultipleSelect);
    window.addEventListener('keyup', enableMultipleSelect);
    return () => {
      window.removeEventListener('keydown', enableMultipleSelect);
      window.removeEventListener('keyup', enableMultipleSelect);
    };
  }, []);
  const backwardDisabled =
    rootLayer?.data.child.findIndex((i) => selectedLayerIds.includes(i)) === 0;
  const forwardDisabled =
    rootLayer?.data.child.findLastIndex((i) => selectedLayerIds.includes(i)) ===
    (rootLayer?.data.child.length || 0) - 1;
  const activeLayer = useMemo(
    () => (layerList || []).find((layer) => layer.id === activeId),
    [layerList, activeId]
  );
  const handleSelectLayer = (layerId: LayerId) => {
    actions.selectLayers(
      activePage,
      layerId,
      dataRef.current.isMultipleSelect ? 'add' : 'replace'
    );
  };

  const handleAlign = (
    alignment: 'left' | 'right' | 'center' | 'top' | 'bottom' | 'middle'
  ) => {
    actions.setAlign(alignment);
    actions.hideContextMenu();
  };

  const handleForward = () => {
    if (!forwardDisabled) {
      actions.bringForward(pageIndex, selectedLayerIds);
      actions.hideContextMenu();
    }
  };
  const handleToFront = () => {
    if (!forwardDisabled) {
      actions.bringToFront(pageIndex, selectedLayerIds);
      actions.hideContextMenu();
    }
  };
  const handleBackward = () => {
    if (!backwardDisabled) {
      actions.sendBackward(pageIndex, selectedLayerIds);
      actions.hideContextMenu();
    }
  };
  const handleToBack = () => {
    if (!backwardDisabled) {
      actions.sendToBack(pageIndex, selectedLayerIds);
      actions.hideContextMenu();
    }
  };

  return (
    <Sidebar {...props}>
      <PageContext.Provider value={{ pageIndex: activePage }}>
        <div
          css={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              height: 48,
              borderBottom: '1px solid rgba(57,76,96,.15)',
              padding: '0 10px',
            }}
            >
            
            <p
              css={{
                lineHeight: '48px',
                textAlign: 'center',
                fontWeight: 800,
                color: tab == 'layers' ? '#181C32' : '#009ef7',
                flexGrow: 1,
                cursor: 'pointer'
              }}
              onClick={() => {
                setTab('arrange');
              }}
            >
              Arrange
            </p>
            <p
              css={{
                lineHeight: '48px',
                textAlign: 'center',
                fontWeight: 800,
                color: tab == 'arrange' ? '#181C32' : '#009ef7',
                flexGrow: 1,
                cursor: 'pointer'
              }}
              onClick={() => {
                setTab('layers');
              }}
            >
              Layers
            </p>
            <div
              css={{
                fontSize: 20,
                flexShrink: 0,
                width: 32,
                height: 32,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={() => {
                actions.setSidebar();
              }}
            >
              <XIcon />
            </div>
          </div>
          {tab === 'layers' && (
            <DndContext
              modifiers={[restrictToVerticalAxis]}
              sensors={sensors}
              onDragCancel={() => setActiveId(null)}
              onDragEnd={handleDragEnd}
              onDragStart={({ active }) => {
                if (!active) {
                  return;
                }
                setActiveId(active.id as LayerId);
              }}
            >
              <div
                ref={ref}
                css={{
                  flexGrow: 1,
                  overflowY: 'auto',
                }}
              >
                <div
                  css={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0,1fr)',
                    gridRowGap: 8,
                    padding: 16,
                  }}
                >
                  <SortableContext
                    items={(layerList || []).map((layer) => layer.id)}
                    strategy={rectSortingStrategy}
                  >
                    {createPortal(
                      <DragOverlay>
                        {activeLayer ? (
                          <LayerContent layer={activeLayer} />
                        ) : null}
                      </DragOverlay>,
                      document.body
                    )}
                    {(layerList || []).map((layer) => (
                      <SortableLayer
                        key={layer.id}
                        layer={layer}
                        onClick={() => handleSelectLayer(layer.id)}
                        onOpenOption={handleClickOption}
                      />
                    ))}
                  </SortableContext>
                  {rootLayer && (
                    <div
                      css={{
                        background: '#F6F6F6',
                        borderRadius: 8,
                        padding: 8,
                        cursor: 'pointer',
                        position: 'relative',
                        borderWidth: 2,
                        borderStyle: 'solid',
                        borderColor: selectedLayerIds.includes(rootLayer.id)
                          ? '#3d8eff'
                          : 'transparent',
                      }}
                      onMouseDown={() => handleSelectLayer(rootLayer.id)}
                    >
                      <div
                        css={{
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          css={{
                            width: 40,
                            height: 40,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexShrink: 0,
                          }}
                        ></div>
                        <div css={{ minWidth: 0, flexGrow: 1 }}>
                          <ReverseTransformLayer
                            hiddenChild={true}
                            layer={rootLayer}
                          />
                        </div>

                        <div css={{ flexShrink: 0, fontSize: 24 }}>
                          <SelectionBackgroundIcon />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </DndContext>
          )}
          {tab === 'arrange' && (
            <div>
              <div>
                <div css={{
                  margin: '20px 25px 20px',
                  fontSize: '13px'
                  }}>Align to page</div>
                <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'row',
                  padding: '0 20px 20px',
                  textAlign: 'center',
                  borderBottom: '1px solid #d9d9d9'
                }}
                >
                  <div
                  css={{
                    display: 'flex',
                    alignItems: 'left',
                    flexDirection: 'column',
                    padding: '0 10px',
                    textAlign: 'left',
                    gap: '20px',
                    width: '100%',
                    borderRight: '1px solid #d9d9d9'
                  }}
                  >
                    <div css={{
                      ':hover': {
                        color: '#009ef7',
                      },
                      cursor: 'pointer'
                    }}
                    onClick={()=>{ handleAlign('top') }}
                    >
                      <AlignTopIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>Top</span>
                    </div>
                    <div css={{
                      ':hover': {
                        color: '#009ef7',
                      },
                      cursor: 'pointer'
                    }}
                    onClick={()=>{ handleAlign('middle') }}
                    >
                      <AlignCenterVerticalIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>Middle</span>
                    </div>
                    <div css={{
                      ':hover': {
                        color: '#009ef7',
                      },
                      cursor: 'pointer'
                    }}
                    onClick={()=>{ handleAlign('bottom') }}
                    >
                      <AlignBottomIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>Bottom</span>
                    </div>
                  </div>
                  <div
                  css={{
                    display: 'flex',
                    alignItems: 'left',
                    flexDirection: 'column',
                    padding: '0 10px',
                    textAlign: 'left',
                    marginLeft: '20px',
                    gap: '20px',
                    width: '100%'
                  }}
                  >
                    <div css={{
                      ':hover': {
                        color: '#009ef7',
                      },
                      cursor: 'pointer'
                    }}
                    onClick={()=>{ handleAlign('left') }}
                    >
                      <AlignLeftIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>Left</span>
                    </div>
                    <div css={{
                      ':hover': {
                        color: '#009ef7',
                      },
                      cursor: 'pointer'
                    }}
                    onClick={()=>{ handleAlign('center') }}
                    >
                      <AlignCenterHorizontalIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>Center</span>
                    </div>
                    <div css={{
                      ':hover': {
                        color: '#009ef7',
                      },
                      cursor: 'pointer'
                    }}
                    onClick={()=>{ handleAlign('right') }}
                    >
                      <AlignRightIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>Right</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
              <div css={{
                margin: '20px 25px 20px',
                fontSize: '13px'
                }}></div>
              <div
              css={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                padding: '0 20px 20px',
                textAlign: 'center',
                borderBottom: '1px solid #d9d9d9'
              }}
              >
                <div
                css={{
                  display: 'flex',
                  alignItems: 'left',
                  flexDirection: 'column',
                  padding: '0 10px',
                  textAlign: 'left',
                  gap: '20px',
                  width: '100%',
                  borderRight: '1px solid #d9d9d9'
                }}
                >
                  <div css={{
                    color: forwardDisabled ? '#aaa' : '#5E6278',
                    ':hover': {
                      color: forwardDisabled ? '#aaa' : '#009ef7',
                    },
                    cursor: forwardDisabled ? 'auto' : 'pointer'
                  }}
                  onClick={()=>{ handleForward() }}
                  >
                    <CaretCircleUpIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>Forward</span>
                  </div>
                  <div css={{
                    color: forwardDisabled ? '#aaa' : '#5E6278',
                    ':hover': {
                      color: forwardDisabled ? '#aaa' : '#009ef7',
                    },
                    cursor: forwardDisabled ? 'auto' : 'pointer'
                  }}
                  onClick={()=>{ handleToFront() }}
                  >
                    <CaretCircleDoubleUpIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>To Front</span>
                  </div>
                  
                </div>
                <div
                css={{
                  display: 'flex',
                  alignItems: 'left',
                  flexDirection: 'column',
                  padding: '0 10px',
                  textAlign: 'left',
                  marginLeft: '20px',
                  gap: '20px',
                  width: '100%'
                }}
                >
                  <div css={{
                    color: backwardDisabled ? '#aaa' : '#5E6278',
                    ':hover': {
                      color: backwardDisabled ? '#aaa' : '#009ef7',
                    },
                    cursor: backwardDisabled ? 'auto' : 'pointer'
                  }}
                  onClick={()=>{ handleBackward() }}
                  >
                    <CaretCircleDownIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>Backward</span>
                  </div>
                  <div css={{
                    color: backwardDisabled ? '#aaa' : '#5E6278',
                    ':hover': {
                      color: backwardDisabled ? '#aaa' : '#009ef7',
                    },
                    cursor: backwardDisabled ? 'auto' : 'pointer'
                  }}
                  onClick={()=>{ handleToBack() }}
                  >
                    <CaretCircleDoubleDownIcon css={{zoom: 1.5, marginRight: '5px', display: 'inline-block'}}/><span>To Back</span>
                  </div>
                </div>
              </div>
            </div>
            </div>
          )}
        </div>
      </PageContext.Provider>
    </Sidebar>
  );
};

export default forwardRef<HTMLDivElement, LayerSidebarProps>(LayerSidebar);
